import React, { useContext, useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, type TextStyle } from 'react-native';
import { router, type Href } from 'expo-router';
import {
  Plus,
  LogIn,
  HelpCircle,
  Briefcase,
  Users,
  ClipboardList,
  ChevronRight,
} from 'lucide-react-native';
import type { LucideIcon } from 'lucide-react-native';
import {
  useTheme,
  SPACING,
  BORDER_RADIUS,
  FONT_SIZES,
  TYPOGRAPHY,
  SIDEBAR_WIDTH,
  SIDEBAR_RAIL_WIDTH,
  SIDEBAR_FOOTER_HEIGHT,
  SHELL_HEADER_HEIGHT,
  SHELL_RULE_WIDTH,
  Z_INDEX,
  withWeight,
} from '../../theme';
import { ALL_TABS } from '@constants/navigationConfig';
import { TAB_ROUTE_MAP, useActiveTab } from '@hooks/useTabNavigation';
import { useWebHover } from '@hooks/useWebHover';
import { useOpenMapPathsOverlay } from '@hooks/useOpenMapPathsOverlay';
import { useOpenProperty } from '@hooks/useOpenProperty';
import { CURSOR_POINTER, TRANSITION_HOVER, TRANSITION_TRANSFORM } from '@utils/webStyles';
import { useUIStore } from '../../store/uiStore';
import { useAuthStore } from '../../store/authStore';
import { useAuthGateStore } from '../../store/authGateStore';
import { useNotificationsStore } from '../../store/notificationsStore';
import { useAuthGate } from '@hooks/useAuthGate';
import { Avatar, BrandTile, Popover, RediLogo } from '../common';
import { useActiveBrand } from '../../hooks/useActiveBrand';
import { CreateActionMenuItems } from './CreateActionSheet';
import type { SidebarVariant } from '@utils/sidebarVariant';

export type { SidebarVariant };

// The rail's hover label is drawn once, by SidebarNav, as the last child of
// the sidebar's outer View. A row can't draw its own: the nav list is a
// ScrollView, a scroll container clips both axes (CSS turns overflow-x:
// visible into auto when the other axis scrolls), and its content box also
// anchors position: fixed. Either way the label was cut off at the rail's
// 64px edge on dev (Sept 16 2026). Rows report hover through this context.
type RailTooltip = { label: string; rect: DOMRect } | null;
const RailTooltipContext = React.createContext<((tooltip: RailTooltip) => void) | null>(null);

// Half the label's height (caption line height plus its vertical padding),
// so the label centers on the row.
const RAIL_TOOLTIP_HALF_HEIGHT = (TYPOGRAPHY.caption.lineHeight + 2 * SPACING.xxs) / 2;

interface SidebarNavProps {
  /** 'full' (default): the 240px sidebar with labels. 'rail': the map
   *  screen's 64px icon-only rail (SIDEBAR_RAIL_WIDTH). */
  variant?: SidebarVariant;
  /** Rail only: the map-tools rows (Layers, Key, Present, Plan) pinned to
   *  the bottom, below the nav ScrollView. A ReactNode so this component
   *  never has to import anything from the map — MapRailTools.web.tsx owns
   *  its own store subscriptions and is wired in from app/_layout.tsx. */
  mapTools?: React.ReactNode;
}

interface NavRowProps {
  /** Keyboard shortcut letter shown in the rail hover label only, never in
   *  the accessible name (see useGlobalShortcuts.web.ts). */
  shortcut?: string;
  icon: LucideIcon;
  label: string;
  onPress: () => void;
  isActive?: boolean;
  dimmed?: boolean;
  showDot?: boolean;
  testID?: string;
  /** True while an insurer brand is active, for a main-tab row only (agent
   *  rows are never active, so they never pass this). Swaps the active
   *  icon/label color from ink to the brand accent, matching the rail. */
  branded?: boolean;
  /** Icon-only rendering for the map screen's collapsed rail: fixed square
   *  button, centered; the label becomes a hover tooltip instead of inline
   *  text. testID stays the same as the full row so audits and Maestro can
   *  click it identically in either variant. */
  railMode?: boolean;
  /** True for a row that toggles something on/off rather than navigating to
   *  a page — the map-tools rail (RailToolButton below). Swaps the active
   *  indicator from the shared sliding rail (a single "current route"
   *  pointer — wrong when several of these can be on at once) to a
   *  persistent fill on the row itself, and reports state via aria-pressed
   *  instead of accessibilityState (RN-web drops the latter on export —
   *  same reasoning as MapControls.tsx's layers button). */
  toggle?: boolean;
  /** Tools draw a touch lighter than nav rows. */
  iconStrokeWidth?: number;
}

function NavRow({
  icon: Icon,
  label,
  onPress,
  isActive = false,
  dimmed = false,
  showDot = false,
  testID,
  branded = false,
  railMode = false,
  toggle = false,
  iconStrokeWidth = 2,
  shortcut,
}: NavRowProps) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  const activeColor = branded ? colors.primary : colors.text;
  // Rail rows report hover up to SidebarNav, which draws the label outside
  // the nav ScrollView (see RailTooltipContext).
  const rowRef = useRef<React.ElementRef<typeof TouchableOpacity>>(null);
  const setRailTooltip = useContext(RailTooltipContext);
  useEffect(() => {
    if (!railMode || !setRailTooltip) return;
    if (!hovered) return;
    const node = rowRef.current as unknown as HTMLElement | null;
    if (typeof node?.getBoundingClientRect !== 'function') return;
    const tooltipLabel = shortcut ? `${label} (${shortcut})` : label;
    setRailTooltip({ label: tooltipLabel, rect: node.getBoundingClientRect() });
    return () => setRailTooltip(null);
  }, [railMode, hovered, label, shortcut, setRailTooltip]);

  if (railMode) {
    return (
      <TouchableOpacity
        ref={rowRef}
        onPress={onPress}
        {...hoverProps}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={toggle ? undefined : { selected: isActive }}
        aria-pressed={toggle ? isActive : undefined}
        testID={testID}
        style={[styles.railRow, toggle && styles.railRowTool, CURSOR_POINTER]}>
        <View
          style={[
            styles.railRowClip,
            toggle && styles.railRowClipTool,
            // A toggle's "on" state is a persistent fill — unlike a main
            // tab, several of these can be on together, so there's no
            // single sliding rail to point at the current one.
            toggle && isActive && { backgroundColor: colors.pressedSurface },
            !isActive && hovered && { backgroundColor: colors.hoverSurface },
            dimmed && styles.dimmed,
            TRANSITION_HOVER,
          ]}>
          <Icon
            size={18}
            color={isActive ? activeColor : colors.textSecondary}
            strokeWidth={iconStrokeWidth}
          />
          {showDot && <View style={[styles.railDot, { backgroundColor: colors.primary }]} />}
        </View>
      </TouchableOpacity>
    );
  }

  return (
    <TouchableOpacity
      onPress={onPress}
      {...hoverProps}
      activeOpacity={0.7}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ selected: isActive }}
      testID={testID}
      style={[
        styles.navRow,
        !isActive && hovered && { backgroundColor: colors.hoverSurface },
        dimmed && styles.dimmed,
        TRANSITION_HOVER,
        CURSOR_POINTER,
      ]}>
      {/* The active accent rail is a single shared element that slides between
          rows (see navMainGroup), so it lives in the parent — not here. The
          row itself just carries the primary icon + label color when active,
          in the brand accent instead of ink when a brand is active. */}
      <View
        style={[
          styles.navRowContent,
          TRANSITION_TRANSFORM,
          // Tactile 2px nudge on hover (non-active only); the rail stays put.
          !isActive && hovered && styles.navRowContentHovered,
        ]}>
        {/* Constant stroke + weight: color and the pill fill carry the active
            state, so toggling never causes width/weight jitter. */}
        <Icon size={18} color={isActive ? activeColor : colors.textSecondary} strokeWidth={2} />
        <Text
          style={[
            styles.navLabel,
            { color: isActive ? activeColor : colors.text },
            isActive && styles.navLabelActive,
          ]}>
          {label}
        </Text>
        {showDot && <View style={[styles.dot, { backgroundColor: colors.primary }]} />}
      </View>
    </TouchableOpacity>
  );
}

/**
 * Thin wrapper over NavRow's rail mode for the map-tools slot
 * (MapRailTools.web.tsx renders four of these). Every prop maps straight
 * onto NavRow — this exists so the map layer never has to know NavRow's
 * shape, only "an icon, a label, a press handler, and whether it's on".
 */
export function RailToolButton({
  icon,
  label,
  onPress,
  active = false,
  testID,
  shortcut,
}: {
  icon: LucideIcon;
  label: string;
  onPress: () => void;
  active?: boolean;
  testID?: string;
  shortcut?: string;
}) {
  return (
    <NavRow
      icon={icon}
      label={label}
      onPress={onPress}
      isActive={active}
      testID={testID}
      shortcut={shortcut}
      railMode
      toggle
      iconStrokeWidth={1.75}
    />
  );
}

/**
 * Persistent left navigation for the desktop shell (web >1024px only).
 * Mirrors the floating tab bar's destinations and auth gating, adds the
 * agent workspace section, and hosts the user identity / sign-in affordance.
 * Renders fine signed-out — public routes stay public.
 *
 * `variant='rail'` (the map screen — see AppShell) is a different vertical
 * structure, not just a narrower version of 'full': the identity block
 * (avatar, or Sign in + How REDI works) moves from the footer up to just
 * under the header, and the map tools (`mapTools` prop) pin to the bottom
 * below the nav list instead. There is no footer in rail mode. 'full' keeps
 * its original shape: header, brand tile, nav list, footer identity.
 */
export function SidebarNav({ variant = 'full', mapTools }: SidebarNavProps) {
  const { colors } = useTheme();
  const containerRef = useRef<View>(null);
  const [railTooltip, setRailTooltip] = useState<RailTooltip>(null);
  const brand = useActiveBrand();
  const isActiveTab = useActiveTab();
  const user = useAuthStore((s) => s.user);
  const openSheet = useUIStore((s) => s.openSheet);
  const isUserMenuOpen = useUIStore((s) => s.activeSheet === 'userMenu');
  const hasSeenTour = useUIStore((s) => s.hasSeenTour);
  const hasDismissedSaveNudge = useUIStore((s) => s.hasDismissedSaveNudge);
  // A new SW build is waiting — surface it on the same account-card avatar
  // dot used for the tour/save nudges, since the avatar button (opening the
  // user menu -> Profile) is this shell's Profile entry point. See
  // ProfileFooter for the tap-to-reload affordance.
  const swUpdateAvailable = useUIStore((s) => s.swUpdateAvailable);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const { requireAuth } = useAuthGate();
  const openMapPathsOverlay = useOpenMapPathsOverlay();
  const openProperty = useOpenProperty();
  const { hovered: avatarHovered, hoverProps: avatarHoverProps } = useWebHover();
  const { hovered: createHovered, hoverProps: createHoverProps } = useWebHover();
  // The "+" opens a Popover here instead of the createMenu sheet
  // (panel-system.md Phase 2) — SidebarNav only ever renders on the desktop
  // shell (AppShell gates it), so there's no phone/tablet fork to make: the
  // sheet stays the only presentation there because GlobalFloatingTabBar's
  // own "+" is a separate call site that still calls openSheet('createMenu').
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const createButtonRef = useRef<React.ElementRef<typeof TouchableOpacity>>(null);

  const isRail = variant === 'rail';

  const showAvatarDot =
    (user?.isAnonymous === true && (user?.postsCount ?? 0) > 0 && !hasDismissedSaveNudge) ||
    swUpdateAvailable;

  const isAnon = user?.isAnonymous === true;
  const fullName = isAnon
    ? 'Fire Scout'
    : `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'User';
  // Quiet secondary line for the account card (full variant only).
  const accountSubtitle = isAnon ? 'Guest' : (user?.agentProfile?.insurerName ?? 'Member');

  // The shared active rail slides to whichever main tab is active. Only the
  // main tabs are ever "active" (agent rows pass isActive={false}), so the
  // rail tracks an index into this list. -1 (e.g. on an agent route) hides it.
  // Row height (40) + list gap (xxs = 2) is the same in both variants, so
  // this pitch and the rail's translateY math need no branching for rail.
  const mainTabs = ALL_TABS.filter((t) => !t.isCreate);
  const activeIndex = mainTabs.findIndex((t) => isActiveTab(t.name));
  const RAIL_ROW_PITCH = 42;

  const openUserMenu = () => {
    // Rail mode: the trigger sits at the bottom of the rail, under the map
    // tools tray, not the footer — 'rail' tells UserMenuDropdown to open
    // beside it there instead of from the bottom-left (see that
    // component's anchor switch).
    openSheet('userMenu', isRail ? { userMenuAnchor: 'rail' } : undefined);
  };

  // Label position in the sidebar's own coordinates, from the hovered row's
  // viewport box and this container's.
  const containerNode = containerRef.current as unknown as HTMLElement | null;
  const tooltipPos =
    railTooltip && typeof containerNode?.getBoundingClientRect === 'function'
      ? (() => {
          const c = containerNode.getBoundingClientRect();
          const r = railTooltip.rect;
          return {
            top: r.top - c.top + r.height / 2 - RAIL_TOOLTIP_HALF_HEIGHT,
            left: r.right - c.left + SPACING.xs,
          };
        })()
      : null;

  return (
    <RailTooltipContext.Provider value={setRailTooltip}>
      <View
        ref={containerRef}
        testID="sidebar-nav"
        // The printed readiness record hides everything marked print-hide, or the
        // sidebar lands in the middle of the PDF. react-native-web needs `dataSet`
        // for a data-* attribute to reach the DOM at all, and @media print means
        // this changes nothing on screen. Set here rather than on a wrapper in
        // AppShell: a plain wrapper View does not stretch in that flex row and
        // left the sidebar short of full height.
        {...({ dataSet: { printHide: 'true', sidebarVariant: variant } } as object)}
        style={[
          styles.container,
          // Flat tint (surface, not surfaceElevated) so the sidebar reads as
          // chrome against the white content pane. Design B has no gradient
          // wash anywhere — the hairline and the tone carry the separation.
          { backgroundColor: colors.surface, borderRightColor: colors.border },
          // Instant, not animated: Leaflet's ResizeObserver (Map.web.tsx)
          // calls invalidateSize on every width change this container makes,
          // and a CSS width transition would fire that continuously across
          // the whole 150ms, redrawing/jittering tiles mid-transition instead
          // of resizing once cleanly. A dock panel's own slide (TRANSITION_
          // DOCK_INSET) animates chrome that floats OVER the map, not the
          // map's own container box, which is why that one is safe to ease
          // and this one isn't.
          { width: isRail ? SIDEBAR_RAIL_WIDTH : SIDEBAR_WIDTH, zIndex: Z_INDEX.sidebar },
        ]}>
        {isRail ? (
          // The small "redi" wordmark, not the flame alone (the panel-system
          // prototype's rail, Jason Sept 28): the name reads at 64px.
          <View style={styles.railHeaderMark}>
            <RediLogo fontSize={FONT_SIZES.md} />
          </View>
        ) : (
          /* Single header bar: wordmark on the left, a compact create action on
           the right. The "+" reuses the create motif from the mobile tab bar,
           so it reads unmistakably as "new post" while keeping the header
           tidy. */
          <View style={styles.headerRow}>
            <RediLogo fontSize={FONT_SIZES.xl} />
            <TouchableOpacity
              ref={createButtonRef}
              onPress={() => setCreateMenuOpen((v) => !v)}
              {...createHoverProps}
              activeOpacity={0.85}
              accessibilityRole="button"
              accessibilityLabel="New post"
              testID="sidebar-create"
              // Eased scale on hover: the ink fill can't take an overlay without
              // covering the icon, so the lift is the hover feedback.
              style={[
                styles.createButton,
                TRANSITION_TRANSFORM,
                createHovered && styles.createButtonHovered,
                CURSOR_POINTER,
              ]}>
              {/* Flat ink circle — gradients left the grammar with Design B. */}
              <View style={[styles.createFill, { backgroundColor: colors.action }]}>
                <Plus size={18} color={colors.actionOn} strokeWidth={2.5} />
              </View>
            </TouchableOpacity>
          </View>
        )}

        {!isRail && (
          <View style={[styles.headerDivider, { backgroundColor: colors.borderLight }]} />
        )}

        {isRail && (
          <>
            {/* The "+" sits below the mark, with clear space from the mark
                above and the nav rows below. The identity block (avatar or
                Sign in) lives at the very bottom of the rail, under the map
                tools tray — see the mapTools block further down. Same dot
                logic as the full sidebar's account row. */}
            <View style={styles.railCreateRow}>
              <TouchableOpacity
                ref={createButtonRef}
                onPress={() => setCreateMenuOpen((v) => !v)}
                {...createHoverProps}
                activeOpacity={0.85}
                accessibilityRole="button"
                accessibilityLabel="New post"
                aria-expanded={createMenuOpen}
                aria-haspopup="menu"
                testID="sidebar-create"
                style={[
                  styles.createButton,
                  TRANSITION_TRANSFORM,
                  createHovered && styles.createButtonHovered,
                  CURSOR_POINTER,
                ]}>
                {/* While its menu is open the "+" turns to an ×: the menu's
                    tie to its button, instead of a caret (Sept 28, the same
                    as every other menu here, none of which has one). */}
                <View style={[styles.createFill, { backgroundColor: colors.action }]}>
                  <View style={createMenuOpen ? styles.createIconOpen : undefined}>
                    <Plus size={18} color={colors.actionOn} strokeWidth={2.5} />
                  </View>
                </View>
              </TouchableOpacity>
            </View>
          </>
        )}

        {/* Renders null with no active brand, so an unbranded sidebar stays
          pixel-identical to before this tile existed. Rail mode drops the
          insurer name/program line (no room at 64px) and keeps only the
          logo mark, or the shield fallback — see BrandTile's 'rail' variant. */}
        <BrandTile
          variant={isRail ? 'rail' : 'sidebar'}
          style={isRail ? styles.brandTileRail : styles.brandTile}
        />

        <ScrollView
          style={styles.navScroll}
          contentContainerStyle={[styles.navList, isRail && styles.navListRail]}>
          {!isRail && (
            <Text
              style={[
                styles.sectionLabel,
                styles.sectionLabelFirst,
                { color: colors.textSecondary },
              ]}>
              Navigate
            </Text>
          )}
          <View style={styles.navMainGroup}>
            {/* Shared active rail — one element that slides between rows. Kept
              at the group's left edge in rail mode too: at 64px it reads as
              a current-tab accent bar along the sidebar's own left edge,
              needing no extra positioning logic. */}
            <View
              style={[
                styles.sharedRail,
                TRANSITION_TRANSFORM,
                {
                  backgroundColor: brand ? colors.primary : colors.text,
                  opacity: activeIndex >= 0 ? 1 : 0,
                  transform: [{ translateY: Math.max(0, activeIndex) * RAIL_ROW_PITCH }],
                },
              ]}
            />
            {mainTabs.map((tab) => {
              const isRestricted = !tab.publicTab && !user;
              return (
                <NavRow
                  key={tab.name}
                  icon={tab.icon}
                  label={tab.label}
                  isActive={isActiveTab(tab.name)}
                  dimmed={isRestricted}
                  branded={Boolean(brand)}
                  testID={`sidebar-tab-${tab.name}`}
                  railMode={isRail}
                  onPress={() => {
                    if (tab.overlay === 'mapPaths') {
                      openMapPathsOverlay();
                    } else if (tab.action === 'openProperty') {
                      openProperty();
                    } else if (isRestricted) {
                      requireAuth(() => router.push(TAB_ROUTE_MAP[tab.name] as Href), {
                        message: `Sign in to access ${tab.label}`,
                        allowAnonymous: true,
                      });
                    } else {
                      router.push(TAB_ROUTE_MAP[tab.name] as Href);
                    }
                  }}
                />
              );
            })}
          </View>

          {user?.agentProfile && (
            <>
              {isRail ? (
                <View
                  style={[styles.railSectionDivider, { backgroundColor: colors.borderLight }]}
                />
              ) : (
                <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>
                  {user.agentProfile.insurerName || 'Agent'}
                </Text>
              )}
              <NavRow
                icon={Briefcase}
                label="Agent home"
                isActive={false}
                onPress={() => router.push('/agent' as Href)}
                testID="sidebar-agent-home"
                railMode={isRail}
              />
              <NavRow
                icon={Users}
                label="Client properties"
                isActive={false}
                onPress={() => router.push('/agent/clients' as Href)}
                testID="sidebar-agent-clients"
                railMode={isRail}
              />
              <NavRow
                icon={ClipboardList}
                label="Checklists"
                isActive={false}
                onPress={() => router.push('/agent/checklists' as Href)}
                testID="sidebar-agent-checklists"
                railMode={isRail}
              />
            </>
          )}
        </ScrollView>

        {isRail ? (
          // Pinned to the bottom, outside the nav ScrollView so it never
          // scrolls away and its position never shifts with agent rows or
          // the brand tile above. The rail renders no footer beyond this.
          mapTools != null && (
            <>
              <View
                style={[
                  styles.mapToolsRail,
                  { backgroundColor: colors.surface, borderColor: colors.border },
                ]}>
                {mapTools}
              </View>
              {/* Identity at the very bottom, under the map tools. Signed
                  out shows only Sign in; the tour lives in the user menu
                  and the Guidance overlay. */}
              <View style={styles.railIdentityBlock}>
                {user ? (
                  <TouchableOpacity
                    onPress={openUserMenu}
                    {...avatarHoverProps}
                    activeOpacity={0.7}
                    accessibilityRole="button"
                    accessibilityLabel="Open user menu"
                    testID="sidebar-avatar-button"
                    style={[
                      styles.railAvatarButton,
                      avatarHovered && { backgroundColor: colors.hoverSurface },
                      TRANSITION_HOVER,
                      CURSOR_POINTER,
                    ]}>
                    <Avatar
                      size={32}
                      name={isAnon ? '' : fullName}
                      imageUrl={isAnon ? null : user.avatarUrl}
                      anonymous={isAnon}
                      showIndicator={
                        (!hasSeenTour || showAvatarDot || unreadCount > 0) && !isUserMenuOpen
                      }
                    />
                  </TouchableOpacity>
                ) : (
                  <NavRow
                    icon={LogIn}
                    label="Sign in"
                    onPress={() =>
                      useAuthGateStore
                        .getState()
                        .open(() => {}, { message: 'Sign in to access your account' })
                    }
                    testID="sidebar-sign-in-button"
                    railMode
                  />
                )}
              </View>
            </>
          )
        ) : (
          <View
            style={[
              styles.footer,
              user ? styles.footerIdentity : styles.footerSignedOut,
              { borderTopColor: colors.border },
            ]}>
            {user ? (
              <TouchableOpacity
                onPress={openUserMenu}
                {...avatarHoverProps}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel="Open user menu"
                testID="sidebar-avatar-button"
                style={[
                  styles.accountRow,
                  // Hairline row, not a boxed card: the footer's top rule is the
                  // only edge. Hover washes the row the same way NavRow does, so
                  // the identity affordance matches the nav above it.
                  avatarHovered && { backgroundColor: colors.hoverSurface },
                  TRANSITION_HOVER,
                  CURSOR_POINTER,
                ]}>
                <Avatar
                  size={32}
                  name={isAnon ? '' : fullName}
                  imageUrl={isAnon ? null : user.avatarUrl}
                  anonymous={isAnon}
                  // Unread alerts joined the dot when the Alerts tab left the
                  // nav — the user menu is now the only path to /alerts.
                  showIndicator={
                    (!hasSeenTour || showAvatarDot || unreadCount > 0) && !isUserMenuOpen
                  }
                />
                <View style={styles.accountText}>
                  <Text style={[styles.identityName, { color: colors.text }]} numberOfLines={1}>
                    {fullName}
                  </Text>
                  <Text
                    style={[styles.accountSubtitle, { color: colors.textSecondary }]}
                    numberOfLines={1}>
                    {accountSubtitle}
                  </Text>
                </View>
                <ChevronRight size={16} color={colors.textTertiary} strokeWidth={2} />
              </TouchableOpacity>
            ) : (
              <View style={styles.signedOutFooter}>
                <NavRow
                  icon={HelpCircle}
                  label="How REDI works"
                  showDot={!hasSeenTour}
                  onPress={() => openSheet('howItWorks')}
                  testID="sidebar-help-button"
                />
                <NavRow
                  icon={LogIn}
                  label="Sign in"
                  onPress={() =>
                    useAuthGateStore
                      .getState()
                      .open(() => {}, { message: 'Sign in to access your account' })
                  }
                  testID="sidebar-sign-in-button"
                />
              </View>
            )}
          </View>
        )}
        {railTooltip && tooltipPos && (
          <View
            pointerEvents="none"
            testID="sidebar-rail-tooltip"
            style={[
              styles.railTooltip,
              { top: tooltipPos.top, left: tooltipPos.left, backgroundColor: colors.action },
            ]}>
            <Text style={[styles.railTooltipText, { color: colors.actionOn }]}>
              {railTooltip.label}
            </Text>
          </View>
        )}
        <Popover
          visible={createMenuOpen}
          anchorRef={createButtonRef}
          onClose={() => setCreateMenuOpen(false)}
          placement="right-start"
          gap={SPACING.sm}
          minLeft={(isRail ? SIDEBAR_RAIL_WIDTH : SIDEBAR_WIDTH) + SPACING.xs}
          testID="create-menu-popover"
          accessibilityLabel="Create">
          <CreateActionMenuItems onDismiss={() => setCreateMenuOpen(false)} variant="popover" />
        </Popover>
      </View>
    </RailTooltipContext.Provider>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRightWidth: 1,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    // Left inset (md + xs = 20) aligns the wordmark with the nav icon column
    // (8 list inset + 12 row padding); the create button hugs the right edge.
    paddingLeft: SPACING.md + SPACING.xs,
    paddingRight: SPACING.md,
    // Fixed, not padded: the divider below has to land on the same y as the
    // content pane's header rule, and padding + whatever the logo measures
    // put it at 72 while the content rule sat at 78. The 32px create button
    // centers exactly in this height.
    height: SHELL_HEADER_HEIGHT,
  },
  railHeaderMark: {
    alignItems: 'center',
    paddingTop: SPACING.lg,
    paddingBottom: SPACING.md,
  },
  createIconOpen: {
    transform: [{ rotate: '45deg' }],
  },
  railCreateRow: {
    alignItems: 'center',
    paddingBottom: SPACING.md,
  },
  railHeader: {
    height: SHELL_HEADER_HEIGHT,
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.xs,
  },
  createButton: {
    width: 32,
    height: 32,
    // Circle, matching the mockup's black circular "+" (and the mobile tab
    // bar's create button). It was a rounded square from the pre-Design-B
    // gradient treatment.
    borderRadius: BORDER_RADIUS.full,
    overflow: 'hidden', // clips the ink fill to the round corners
  },
  createButtonHovered: {
    transform: [{ scale: 1.06 }],
  },
  createFill: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerDivider: {
    height: SHELL_RULE_WIDTH,
    marginHorizontal: SPACING.sm,
    marginBottom: SPACING.sm,
  },
  railIdentityBlock: {
    alignItems: 'center',
    paddingTop: SPACING.smd,
    paddingBottom: SPACING.md,
  },
  railIdentitySignedOut: {
    width: '100%',
    alignItems: 'center',
    gap: SPACING.xxs,
  },
  brandTile: {
    marginHorizontal: SPACING.sm,
    marginBottom: SPACING.xs,
  },
  brandTileRail: {
    alignSelf: 'center',
    marginBottom: SPACING.xs,
  },
  navScroll: {
    flex: 1,
  },
  navList: {
    paddingHorizontal: SPACING.sm,
    gap: SPACING.xxs,
  },
  navListRail: {
    paddingHorizontal: SPACING.xs,
    alignItems: 'center',
  },
  navRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: SPACING.smd,
    height: 40,
    borderRadius: BORDER_RADIUS.md,
    // Clips the soft pill to its rounded corners and keeps the active rail
    // tucked against the row's left edge.
    overflow: 'hidden',
  },
  navRowContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.smd,
  },
  navRowContentHovered: {
    transform: [{ translateX: 2 }],
  },
  railRow: {
    width: '100%',
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  railRowTool: {
    height: 32,
  },
  railRowClipTool: {
    // Tool pills sit inside the tray, so they're a size down from nav
    // pills to leave a gutter between pill and tray edge, and the tray
    // clear of the rail edges (64 rail, 50 tray, 32 pill).
    width: 32,
    height: 28,
  },
  railRowClip: {
    width: 40,
    height: 32,
    borderRadius: BORDER_RADIUS.md,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  railDot: {
    position: 'absolute',
    top: 2,
    right: 2,
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  railTooltip: {
    position: 'absolute',
    justifyContent: 'center',
    paddingVertical: SPACING.xxs,
    paddingHorizontal: SPACING.sm,
    borderRadius: BORDER_RADIUS.sm,
    zIndex: Z_INDEX.dropdown,
  },
  railTooltipText: {
    ...withWeight(TYPOGRAPHY.caption, 'semibold'),
    // Web-only: keep a two-word label ("How REDI works") on one line.
    whiteSpace: 'nowrap',
  } as TextStyle,
  railSectionDivider: {
    height: SHELL_RULE_WIDTH,
    width: 32,
    alignSelf: 'center',
    marginTop: SPACING.sm,
    marginBottom: SPACING.xs,
  },
  navMainGroup: {
    position: 'relative',
    gap: SPACING.xxs,
  },
  sharedRail: {
    position: 'absolute',
    left: 0,
    top: 11, // centers an 18px rail in the first 40px row; translateY tracks active index
    width: 3,
    height: 18,
    borderRadius: 2,
    zIndex: 1, // paints over the rows' hover wash at the left edge
  },
  navLabel: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
    flex: 1,
  },
  navLabelActive: {
    fontFamily: TYPOGRAPHY.h4.fontFamily, // semibold carries the active state
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  sectionLabel: {
    // overline (wider 1.2 tracking, semibold) reads as a deliberate section
    // header rather than a generic small label. Paired with textSecondary,
    // not textTertiary: at 10px on the sidebar's `surface` tint, tertiary
    // measures 2.53 against a 4.5 threshold (audit:a11y). The gradient that
    // used to sit here hid this from axe, which couldn't resolve a
    // background colour through it.
    ...TYPOGRAPHY.overline,
    // Whitespace alone separates the section — a divider on top of a
    // labeled break is double separation.
    paddingTop: SPACING.lg,
    paddingHorizontal: SPACING.smd,
    paddingBottom: SPACING.xs,
  },
  sectionLabelFirst: {
    // First label sits just under the header divider, so it needs less air
    // above than a mid-list section break.
    paddingTop: SPACING.sm,
  },
  dimmed: {
    opacity: 0.5,
  },
  footer: {
    borderTopWidth: 1,
    paddingHorizontal: SPACING.sm,
  },
  footerIdentity: {
    height: SIDEBAR_FOOTER_HEIGHT,
    justifyContent: 'center',
  },
  footerSignedOut: {
    paddingVertical: SPACING.sm,
  },
  mapToolsRail: {
    // A bordered tray so the four toggles read as a toolbox, not as more
    // navigation.
    alignSelf: 'center',
    alignItems: 'center',
    gap: SPACING.xs,
    paddingVertical: SPACING.sm,
    paddingHorizontal: SPACING.sm,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    marginBottom: SPACING.xs,
  },
  railAvatarButton: {
    width: 40,
    height: 40,
    borderRadius: BORDER_RADIUS.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  accountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingVertical: SPACING.xs,
    paddingHorizontal: SPACING.sm,
    borderRadius: BORDER_RADIUS.md, // clips the hover wash only
  },
  accountText: {
    flex: 1,
    justifyContent: 'center',
  },
  identityName: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
  },
  accountSubtitle: {
    ...TYPOGRAPHY.caption,
    marginTop: 1,
  },
  signedOutFooter: {
    gap: SPACING.xxs,
  },
});
